import { asText } from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import RenderFields from '@remote-com/mushu/src/components/Form/DynamicForm/RenderFields';
import { getInitialValues } from '@remote-com/mushu/src/components/Form/DynamicForm/helpers';
import { SuccessTitle, SuccessDescription } from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import {
  InputsContainer,
  VerticallyCenteredForm,
} from '@remote-com/mushu/src/domains/registration/onboarding/shared/styles/Form';
import { getValidationSchema } from '@remote-com/mushu/src/helpers/forms';
import { Formik } from 'formik';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { RichText } from 'prismic-reactjs';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ButtonInline } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import Modal from 'remote-com-shared/components/ModalForm';
import { buildUserName } from 'remote-com-shared/components/ModalForm/helpers';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  CaptchaWrapper,
  FormContainer,
  SubsribeSuccessContainer,
  ModalTitle,
  FormError,
  DynamicFormWrapper,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { BookitScript } from 'remote-com-shared/domains/bookit/Bookit';
import {
  composeBookitPayload,
  formFieldsContainBookit,
} from 'remote-com-shared/domains/bookit/helpers';
import { useBookit } from 'remote-com-shared/domains/bookit/hooks';
import { getFields, handleSpecificHubSpotFields } from 'remote-com-shared/domains/hubspot/fields';
import { useHubspotForm } from 'remote-com-shared/domains/hubspot/hooks';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { LocalisedContentConsumer } from 'remote-com-shared/domains/localisedContent/context';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import freeEmailProviderDomains from 'remote-com-shared/utils/freeEmailProviderList';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import {
  FormWrapper,
  GatedContentSectionWrapper,
  MainContent,
  SectionTitle,
  Description,
  FormButtonsWrapper,
  StyledButton,
  SubmitButton,
  PrimaryButton,
  ChevronIcon,
  StepCounterWrapper,
  StepCounter,
} from './styles';

const { publicRuntimeConfig } = getConfig();
const twoStepFormId = publicRuntimeConfig.HUBSPOT_TWO_STEP_GET_STARTED_FORM_ID;

export function generateAndDownloadDocument(fileUrl) {
  const downloadLink = document.createElement('a');

  downloadLink.href = fileUrl;
  downloadLink.download = true;
  downloadLink.rel = 'noopener';
  downloadLink.click();
}

/**
 * Format our form's values as per HubSpot definition
 * @param {{[fieldName: String]: String}} payload
 */
function getFormattedPayload(payload) {
  // name
  if (typeof payload.name !== 'undefined') {
    const { firstName: firstname, lastName: lastname } = buildUserName(payload.name);
    payload = { ...payload, firstname, lastname };
    delete payload.name;
  }

  return payload;
}

const HUBSPOT_FIELDS_PRIORITY_STAGE = ['email', 'reason', 'target_countries'];

// eslint-disable-next-line no-unused-vars
const propsType = {
  formTitle: PropTypes.string.isRequired,
  formDescription: PropTypes.string.isRequired,
  /**
   * prefill promo code on paid marketing landing pages
   */
  promoCode: PropTypes.string,
  /**
   * to change button label
   */
  buttonLabel: PropTypes.string,
  /**
   * Options for "reasons" field which should activate schedule memo prompt
   */
  scheduleDemoReasonFields: PropTypes.arrayOf(
    PropTypes.shape({ option: PropTypes.string, authType: PropTypes.string })
  ).isRequired,
  hubspotFormDefinition: PropTypes.string.isRequired,
  companyEmailAddressesOnly: PropTypes.bool,
  /**
   * Form identifier, if there are multiple instances of the same form, ensure
   * this is unique.
   */
  formID: PropTypes.string,
};

/**
 * @param {typeof propsType} props
 */
const TwoStepFormRenderedForm = ({
  promoCode,
  buttonLabel,
  scheduleDemoReasonFields,
  hubspotFormDefinition,
  companyEmailAddressesOnly,
  formID = 'two-step-form',
  disablePrimaryCTAIcon = false,
  formTitle, // Custom form title
  formDescription, // Custom form description
}) => {
  const { submitText, inlineMessage: successMessage, formFieldGroups } = hubspotFormDefinition;
  const [usePriorityFields, setUsePriorityFields] = useState(false);
  const STAGES_INDICES = usePriorityFields ? [4] : [5];
  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [currentFormStageIndex, setCurrentFormStageIndex] = useState(0);
  const formContainerRef = useRef(null);
  const impactFormValues = useImpactFormValues();

  const canGoBack = currentFormStageIndex > 0;
  const canGoForward = currentFormStageIndex < STAGES_INDICES.length;

  const validateEmail = (values_) => {
    const errors = {};
    const emailDomain = values_.email.split('@')[1];
    const rejectEmail =
      freeEmailProviderDomains.includes(emailDomain) && !!companyEmailAddressesOnly;
    if (rejectEmail) {
      errors.email = 'Please use a company email address';
    }
    return errors;
  };

  let fields = getFields(formFieldGroups).map((field) => handleSpecificHubSpotFields({ ...field }));

  const shouldLoadBookit = formFieldsContainBookit(fields);
  const {
    submit: submitBookit,
    redirect: redirectBookit,
    ready: bookitIsReady,
    onLoad: initializeBookit,
    scriptConfig,
  } = useBookit();

  useEffect(() => {
    // check for data-priority-fields attribute
    const formDataAttributes = formContainerRef.current.dataset;

    if (formDataAttributes.priorityFields === 'true') {
      setUsePriorityFields(true);
    }
  }, []);

  if (usePriorityFields) {
    const priorityFields = [];
    const restFields = [];

    fields.forEach((field) => {
      if (HUBSPOT_FIELDS_PRIORITY_STAGE.includes(field.name)) {
        priorityFields.push(field);
      } else {
        restFields.push(field);
      }
    });

    fields = [...priorityFields, ...restFields];
  }
  const router = useRouter();
  const referralCode = router?.query?.coupon;
  const [formValues, setFormValues] = useState({
    promo_code: promoCode,
    refer_a_friend_code: referralCode,
  });

  useEffect(() => {
    /**
     * In cases where the form is not embedded in a modal button, the referralCode/other param that
     * may be used in the form is not passed to the Form as a prop initially since we're retrieving
     * that from Next.js' useRouter — so we update the formValues manually later. Formik form also
     * needs to be update with `enableReinitialize`.
     */
    if (typeof referralCode !== 'undefined' && referralCode !== formValues.refer_a_friend_code) {
      setFormValues((prevFormValues) => ({ ...prevFormValues, refer_a_friend_code: referralCode }));
    }
    if (typeof promoCode !== 'undefined' && promoCode !== formValues.promo_code) {
      setFormValues((prevFormValues) => ({ ...prevFormValues, promo_code: promoCode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode, promoCode]);

  const reCaptchaRef = useRef(null);

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  /**
   *
   * @param {Array<{name: string}>} fieldsList
   * @param {number} part
   * @returns
   */
  function getFormStageFields(fieldsList, part) {
    if (part === 0) return fieldsList.slice(0, STAGES_INDICES[0]);

    return fieldsList.slice(STAGES_INDICES[0]);
  }

  async function handleGoNextStage(fieldsDefinitions, { dirty, errors, setTouched, validateForm }) {
    const fieldNames = fieldsDefinitions.map(({ name }) => name);
    const areFieldsValid =
      dirty && !Object.keys(errors).some((fieldName) => fieldNames.includes(fieldName));

    if (areFieldsValid) {
      setCurrentFormStageIndex(1);
    } else {
      const errorFields = {};
      const formErrors = await validateForm();
      const currentFieldsWithErrors = Object.keys(formErrors).filter((value) =>
        fieldNames.includes(value)
      );

      currentFieldsWithErrors.forEach((field) => {
        errorFields[field] = true;
      });
      setTouched(errorFields);
    }
  }
  function handleGoPreviousStage() {
    setCurrentFormStageIndex(0);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }
        const formattedValues = getFormattedPayload(values);

        let payload = {
          ...formattedValues,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        let bookitResponse;
        if (bookitIsReady) {
          bookitResponse = await submitBookit(payload);
          payload = composeBookitPayload(payload, bookitResponse);
        }

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        if (bookitIsReady && bookitResponse) {
          redirectBookit(bookitResponse);
        }

        setShowLoader(false);
        setShowSuccessState(true);
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <>
      {shouldLoadBookit && <BookitScript onLoad={initializeBookit} scriptConfig={scriptConfig} />}

      <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
        <GlobalStyles />
        <FormContainer ref={formContainerRef}>
          {showSuccessState ? (
            <SubsribeSuccessContainer>
              <SuccessMark />
              <SuccessTitle>
                <LocalisedContentConsumer>
                  {({ formSuccessThankYou }) => formSuccessThankYou}
                </LocalisedContentConsumer>
              </SuccessTitle>
              <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            </SubsribeSuccessContainer>
          ) : (
            <DynamicFormWrapper>
              <LocalisedContentConsumer>
                {({ twoStepFormTitle }) =>
                  fieldHasContent(formTitle) ? (
                    <ModalTitle>{formTitle}</ModalTitle>
                  ) : (
                    <ModalTitle>
                      {fieldHasContent(twoStepFormTitle)
                        ? asText(twoStepFormTitle)
                        : 'Start employing anywhere'}
                    </ModalTitle>
                  )
                }
              </LocalisedContentConsumer>
              <LocalisedContentConsumer>
                {({ twoStepFormDescription }) =>
                  fieldHasContent(formDescription) ? (
                    <ModalDescription>{formDescription}</ModalDescription>
                  ) : (
                    <ModalDescription>
                      {fieldHasContent(twoStepFormDescription)
                        ? asText(twoStepFormDescription)
                        : "We'll contact you to schedule a call, answer any questions you may have, and start onboarding your employees."}
                    </ModalDescription>
                  )
                }
              </LocalisedContentConsumer>

              {showFormError && (
                <LocalisedContentConsumer>
                  {({ formErrorDescription }) => (
                    <FormError>
                      <PrismicRichText
                        field={formErrorDescription}
                        externalLinkComponent={ButtonInline}
                        internalLinkComponent={ButtonInline}
                      />
                    </FormError>
                  )}
                </LocalisedContentConsumer>
              )}
              <Formik
                initialValues={getInitialValues(fields, formValues)}
                validate={validateEmail}
                validationSchema={getValidationSchema({ fields })}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({
                  handleSubmit: handleSubmit_,
                  values: values_,
                  setFieldValue,
                  dirty,
                  errors,
                  setTouched,
                  validateForm,
                }) => (
                  <>
                    <VerticallyCenteredForm
                      id={formID}
                      data-testid={formID}
                      onSubmit={handleSubmit_}
                      noValidate
                    >
                      <ThemeProvider theme={{ mode: 'dark', size: 'small' }}>
                        <InputsContainer>
                          <RenderFields
                            fields={getFormStageFields(fields, currentFormStageIndex)}
                            defaultValues={formValues}
                            setFieldValue={setFieldValue}
                            formValues={values_}
                          />
                        </InputsContainer>
                      </ThemeProvider>
                    </VerticallyCenteredForm>

                    {!canGoForward && (
                      <CaptchaWrapper>
                        <LocaleContextConsumer>
                          {(locale) => (
                            <ReCAPTCHA
                              hl={locale}
                              ref={reCaptchaRef}
                              sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                              onChange={handleCaptchaChange}
                            />
                          )}
                        </LocaleContextConsumer>
                      </CaptchaWrapper>
                    )}

                    <FormButtonsWrapper>
                      {canGoBack && (
                        <StyledButton variant="outline" onClick={handleGoPreviousStage} size="xs">
                          <LocalisedContentConsumer>
                            {({ twoStepFormPreviousPage }) => twoStepFormPreviousPage}
                          </LocalisedContentConsumer>
                        </StyledButton>
                      )}
                      {canGoForward ? (
                        <StyledButton
                          onClick={() =>
                            handleGoNextStage(getFormStageFields(fields, currentFormStageIndex), {
                              dirty,
                              errors,
                              setTouched,
                              validateForm,
                            })
                          }
                          size="sm"
                          $primary
                          IconAfter={!disablePrimaryCTAIcon ? ChevronIcon : undefined}
                          id="go-forward-cta-control"
                        >
                          <LocalisedContentConsumer>
                            {({ twoStepFormNextPage }) => twoStepFormNextPage}
                          </LocalisedContentConsumer>
                          <ChevronIcon />
                        </StyledButton>
                      ) : (
                        <SubmitButton
                          isLoading={showLoader}
                          disabled={!isCaptchaDone}
                          // passed since the button is outside of <form />
                          form={formID}
                          size="xs"
                        >
                          {submitText || buttonLabel}
                        </SubmitButton>
                      )}
                    </FormButtonsWrapper>
                    <StepCounterWrapper>
                      <StepCounter>
                        <LocalisedContentConsumer>
                          {({ twoStepFormStepCounter }) =>
                            twoStepFormStepCounter
                              .replace('[currentStep]', currentFormStageIndex + 1)
                              .replace('[totalSteps]', 2)
                          }
                        </LocalisedContentConsumer>
                      </StepCounter>
                    </StepCounterWrapper>
                  </>
                )}
              </Formik>
            </DynamicFormWrapper>
          )}
        </FormContainer>
      </ThemeProvider>
    </>
  );
};

export const TwoStepForm = ({ hubspotFormDefinition, ...props }) => {
  const { isLoading } = hubspotFormDefinition;
  if (isLoading) {
    return null;
  }
  return <TwoStepFormRenderedForm {...props} hubspotFormDefinition={hubspotFormDefinition} />;
};

const GatedContent = (props) => (
  <FormWrapper>
    <TwoStepForm {...props} />
  </FormWrapper>
);

export const GatedContentSection = ({ title, description, ...formProps }) => (
  <GatedContentSectionWrapper>
    <ContentContainer>
      <MainContent>
        <RichText render={title} Component={SectionTitle} $tone="dark" />
        <RichText render={description} Component={Description} $tone="dark" />
      </MainContent>
      <GatedContent {...formProps} />
    </ContentContainer>
  </GatedContentSectionWrapper>
);

export const TwoStepFormModal = ({ visible = false, onCancel, ...props }) => (
  <Modal
    visible={visible}
    onCancel={onCancel}
    footer={null}
    bodyStyle={{ maxWidth: '450px' }}
    {...props}
  >
    <TwoStepForm
      formTitle={props.formTitle}
      formDescription={props.formDescription}
      companyEmailAddressesOnly
      {...props}
    />
  </Modal>
);

export const TwoStepFormModalButton = ({
  children,
  startOpen,
  startOpenViaUrl,
  formName,
  hubspotFormDefinition,
  ...props
}) => {
  const twoStepGetStartedHubspotFormDefinition = useHubspotForm(twoStepFormId);
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (startOpen) {
      setShowModal(true);
    }

    if (startOpenViaUrl) {
      // If formName exists as url parameter with a truthy value
      setShowModal(Boolean(router.query[formName]));
    }
  }, [startOpen, router, startOpenViaUrl, formName]);

  function openModal() {
    setShowModal(true);
  }
  function closeModal() {
    setShowModal(false);
  }
  const { as: buttonAlias, ...buttonProps } = props.button || {};
  const CTAComponent = buttonAlias || props.as || PrimaryButton;

  return (
    <>
      <CTAComponent onClick={openModal} {...(buttonProps || {})}>
        {children}
      </CTAComponent>
      <TwoStepFormModal
        visible={showModal}
        onCancel={closeModal}
        footer={null}
        hubspotFormDefinition={hubspotFormDefinition || twoStepGetStartedHubspotFormDefinition}
        {...props}
      />
    </>
  );
};
